import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2, RefreshCw, Wallet, Printer, CheckCircle2, XCircle, Pencil } from 'lucide-react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchShops } from '../services/shopService';
import {
  fetchPendingShopOrders,
  createShopSettlement,
  fetchShopSettlements,
  fetchShopSettlement,
  updateShopSettlementCommission,
} from '../services/shopSettlementService';
import ShopSettlementEditModal from '../components/ShopSettlementEditModal';
import { openShopSettlementPrintWindow } from '../utils/shopSettlementPrint';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => {
  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(amount);
};

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

const PAYMENT_STATUS_LABELS = {
  0: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  1: { label: 'Paid', className: 'bg-green-100 text-green-700' },
};

export default function GenerateShopSettlement() {
  const [shops, setShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [shopInfo, setShopInfo] = useState(null);
  const [commissionInput, setCommissionInput] = useState('');
  const [orders, setOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersFetched, setOrdersFetched] = useState(false);
  const [settlementDate, setSettlementDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [settlementToEdit, setSettlementToEdit] = useState(null);
  const [editingCommissionId, setEditingCommissionId] = useState(null);
  const [commissionEditValue, setCommissionEditValue] = useState('');
  const [commissionUpdatingId, setCommissionUpdatingId] = useState(null);

  useEffect(() => {
    const loadShops = async () => {
      setLoadingShops(true);
      try {
        const response = await fetchShops({ page: 1, limit: 200, sortBy: 'name', sortOrder: 'ASC' });
        const records = response.data?.data?.records || [];
        setShops(records);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load shops');
      } finally {
        setLoadingShops(false);
      }
    };

    loadShops();
  }, []);

  const commissionRate = useMemo(() => {
    const parsed = Number.parseFloat(commissionInput);
    if (Number.isNaN(parsed) || parsed < 0) {
      return 0;
    }
    return parsed;
  }, [commissionInput]);

  const ordersWithAmounts = useMemo(() => {
    return orders.map((order) => {
      const amount = Number.parseFloat(order.finalAmount || 0) || 0;
      const commissionAmount = Number(((amount * commissionRate) / 100).toFixed(2));
      const payableAmount = Number((amount - commissionAmount).toFixed(2));
      return {
        ...order,
        finalAmount: amount,
        commissionAmount,
        payableAmount,
      };
    });
  }, [commissionRate, orders]);

  const totals = useMemo(() => {
    const selectedSet = new Set(selectedOrderIds);
    return ordersWithAmounts.reduce(
      (accumulator, order) => {
        if (!selectedSet.has(order.orderId)) {
          return accumulator;
        }

        const amount = Number.parseFloat(order.finalAmount || 0) || 0;
        return {
          totalAmount: Number((accumulator.totalAmount + amount).toFixed(2)),
          commissionAmount: Number((accumulator.commissionAmount + order.commissionAmount).toFixed(2)),
          payableAmount: Number((accumulator.payableAmount + order.payableAmount).toFixed(2)),
        };
      },
      { totalAmount: 0, commissionAmount: 0, payableAmount: 0 },
    );
  }, [ordersWithAmounts, selectedOrderIds]);

  const handleShopChange = (event) => {
    const { value } = event.target;
    setSelectedShopId(value);

    if (!value) {
      setCommissionInput('');
      setShopInfo(null);
      return;
    }

    const selectedShop = shops.find((shop) => `${shop.id}` === value);
    if (!selectedShop) {
      setCommissionInput('');
      setShopInfo(null);
      return;
    }

    const normalized = Number.parseFloat(selectedShop.commissionPercentage);
    setCommissionInput(Number.isNaN(normalized) ? '' : `${normalized}`);
    setShopInfo(selectedShop);
  };

  const loadOrders = useCallback(async () => {
    if (!selectedShopId) {
      toast.error('Please select a shop');
      return;
    }

    setLoadingOrders(true);
    setOrdersFetched(false);
    try {
      const params = { shopId: selectedShopId };
      const response = await fetchPendingShopOrders(params);
      const data = response.data?.data || {};
      const pendingOrders = Array.isArray(data.orders) ? data.orders : [];

      setShopInfo(data.shop || null);
      setOrders(pendingOrders);
      setOrdersFetched(true);
      setSelectedOrderIds(pendingOrders.map((order) => order.orderId));

      const resolvedCommission =
        data.totals?.commissionPercentage ?? data.shop?.commissionPercentage ?? '';
      const normalized = Number.parseFloat(resolvedCommission);
      setCommissionInput(Number.isNaN(normalized) ? '' : `${normalized}`);
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || 'Failed to load unsettled orders';
      toast.error(message);
      setOrders([]);
      setSelectedOrderIds([]);
      setOrdersFetched(true);
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedShopId]);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) => {
      if (prev.includes(orderId)) {
        return prev.filter((id) => id !== orderId);
      }
      return [...prev, orderId];
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.length === orders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(orders.map((order) => order.orderId));
    }
  };

  const loadSettlements = useCallback(
    async (shopIdParam) => {
      setLoadingSettlements(true);
      try {
        const params = { page: 1, limit: 20, sortOrder: 'DESC' };
        if (shopIdParam) {
          params.shopId = shopIdParam;
        }
        const response = await fetchShopSettlements(params);
        const records = response.data?.data?.records || [];
        const pending = records.filter((record) => (record.paymentStatus || 0) === 0);
        setSettlements(pending);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load settlements');
      } finally {
        setLoadingSettlements(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadSettlements(selectedShopId || undefined);
  }, [loadSettlements, selectedShopId]);

  const handleCreateSettlement = async () => {
    if (!selectedShopId) {
      toast.error('Select a shop to continue');
      return;
    }
    if (selectedOrderIds.length === 0) {
      toast.error('Select at least one order');
      return;
    }
    if (!settlementDate) {
      toast.error('Select a settlement date');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        shopId: selectedShopId,
        orderIds: selectedOrderIds,
        settlementDate,
        commissionPercentage: commissionRate,
        commissionAmount: totals.commissionAmount,
        amountPaid: 0,
      };
      const response = await createShopSettlement(payload);
      toast.success('Settlement generated successfully');
      await loadSettlements(selectedShopId || undefined);
      const created = response.data?.data;
      if (created) {
        // reload orders to remove settled ones
        await loadOrders();
        // open print view
        await handlePrint(created.id, created);
      }
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || 'Failed to generate settlement';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handlePrint = async (settlementId, presetData) => {
    setPrintingId(settlementId);
    try {
      const settlement = presetData || (await fetchShopSettlement(settlementId)).data?.data;
      if (!settlement) {
        throw new Error('Settlement not found');
      }
      openShopSettlementPrintWindow(settlement);
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || error.message || 'Unable to prepare print view';
      toast.error(message);
    } finally {
      setPrintingId(null);
    }
  };

  const openEditModal = (settlement) => {
    setSettlementToEdit(settlement);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSettlementToEdit(null);
  };

  const editingSettlement = useMemo(() => {
    if (!editingCommissionId) {
      return null;
    }
    return settlements.find((entry) => entry.id === editingCommissionId) || null;
  }, [editingCommissionId, settlements]);

  const editingCommissionAmountPreview = useMemo(() => {
    if (!editingSettlement) {
      return null;
    }

    const parsed = Number.parseFloat(commissionEditValue);
    if (Number.isNaN(parsed)) {
      return null;
    }

    const totalAmount = Number.parseFloat(editingSettlement.totalOrderAmount || 0) || 0;
    return Number(((totalAmount * parsed) / 100).toFixed(2));
  }, [commissionEditValue, editingSettlement]);

  const startCommissionEditing = (settlement) => {
    if (!settlement || (settlement.paymentStatus || 0) === 1) {
      return;
    }

    setEditingCommissionId(settlement.id);
    const initialValue =
      settlement.commissionPercentage !== undefined && settlement.commissionPercentage !== null
        ? Number.parseFloat(settlement.commissionPercentage).toFixed(2)
        : '';
    setCommissionEditValue(initialValue);
  };

  const cancelCommissionEditing = () => {
    setEditingCommissionId(null);
    setCommissionEditValue('');
  };

  const handleCommissionUpdate = async (event) => {
    event?.preventDefault();

    if (!editingSettlement) {
      return;
    }

    if (commissionEditValue === '') {
      toast.error('Enter a commission percentage');
      return;
    }

    const parsed = Number.parseFloat(commissionEditValue);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error('Enter a valid commission percentage');
      return;
    }

    const normalizedPercentage = Number(parsed.toFixed(2));
    const totalAmount = Number.parseFloat(editingSettlement.totalOrderAmount || 0) || 0;
    const computedCommissionAmount = Number(((totalAmount * normalizedPercentage) / 100).toFixed(2));
    const amountPaid = Number.parseFloat(editingSettlement.amountPaid || 0) || 0;
    const payableAmount = Number((totalAmount - computedCommissionAmount).toFixed(2));
    const balanceAmountRaw = Number((payableAmount - amountPaid).toFixed(2));
    const fallbackBalance = Math.abs(balanceAmountRaw) < 0.01 ? 0 : balanceAmountRaw;

    setCommissionUpdatingId(editingSettlement.id);
    try {
      const payload = { commissionPercentage: normalizedPercentage };
      const response = await updateShopSettlementCommission(editingSettlement.id, payload);
      const updated =
        response.data?.data || {
          ...editingSettlement,
          commissionPercentage: normalizedPercentage,
          commissionAmount: computedCommissionAmount,
          balanceAmount: fallbackBalance,
        };

      setSettlements((prev) =>
        prev.map((entry) => (entry.id === editingSettlement.id ? { ...entry, ...updated } : entry)),
      );

      toast.success('Commission updated successfully');
      cancelCommissionEditing();
      await handlePrint(editingSettlement.id, updated);
      await loadSettlements(selectedShopId || undefined);
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || 'Failed to update commission';
      toast.error(message);
    } finally {
      setCommissionUpdatingId(null);
    }
  };

  const handleSettlementUpdated = async () => {
    await loadSettlements(selectedShopId || undefined);
    closeEditModal();
  };

  const isAllSelected = orders.length > 0 && selectedOrderIds.length === orders.length;

  return (
    <>
      <LayoutWithSidebar>
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-2 mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Generate Shop Settlement</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Choose a shop, review unsettled orders, and generate settlement summaries with printable
              copies for your records.
            </p>
          </div>

          <div className="grid gap-6">
            <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div className="flex-1 space-y-4">
                  <div>
                    <label htmlFor="shop" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Select shop
                    </label>
                    <select
                      id="shop"
                      value={selectedShopId}
                      onChange={handleShopChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-900 dark:border-gray-700 h-12"
                      disabled={loadingShops}
                    >
                    <option value="">Choose a shop</option>
                    {shops.map((shop) => (
                      <option key={shop.id} value={shop.id}>
                        {shop.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="commission"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                    >
                      Commission percentage (%)
                    </label>
                    <input
                      id="commission"
                      type="number"
                      min="0"
                      step="0.01"
                      value={commissionInput}
                      onChange={(event) => setCommissionInput(event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-900 dark:border-gray-700"
                      placeholder="Enter commission percentage"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Prefilled with the shop commission when available. Adjust to update order calculations.
                    </p>
                  </div>
                  <div>
                    <label
                      htmlFor="settlementDate"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                    >
                      Settlement date
                    </label>
                    <input
                      id="settlementDate"
                      type="date"
                      value={settlementDate}
                      onChange={(event) => setSettlementDate(event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-900 dark:border-gray-700"
                    />
                  </div>
                </div>
                {shopInfo && (
                  <div className="rounded-md bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 p-3 text-sm text-indigo-900 dark:text-indigo-200">
                    <p className="font-medium">{shopInfo.name}</p>
                    {shopInfo.commissionPercentage ? (
                      <p>Default commission: {Number.parseFloat(shopInfo.commissionPercentage).toFixed(2)}%</p>
                    ) : (
                      <p>No default commission configured for this shop. Enter a rate above.</p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex md:flex-col gap-3">
                <button
                  type="button"
                  onClick={loadOrders}
                  disabled={loadingOrders || !selectedShopId}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-md bg-indigo-600 text-white font-medium shadow-sm hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loadingOrders ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Get unsettled orders
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => loadSettlements(selectedShopId || undefined)}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <RefreshCw className="mr-2 h-4 w-4" /> Refresh settlements
                </button>
              </div>
            </div>
          </section>

          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pending orders</h2>
              {orders.length > 0 && (
                <label className="inline-flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>Select all</span>
                </label>
              )}
            </div>
            <div className="relative overflow-x-auto">
              {loadingOrders && (
                <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/60 flex items-center justify-center z-10">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
              )}
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Select</th>
                    <th className="px-4 py-3 text-left">Order ID</th>
                    <th className="px-4 py-3 text-left">Progress</th>
                    <th className="px-4 py-3 text-left">Payment mode</th>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-right">Commission</th>
                    <th className="px-4 py-3 text-right">Payable</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {ordersWithAmounts.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center text-gray-500 dark:text-gray-300">
                        {ordersFetched
                          ? 'No unsettled orders found for the selected shop.'
                          : 'Select a shop and click "Get unsettled orders" to view pending settlements.'}
                      </td>
                    </tr>
                  ) : (
                    ordersWithAmounts.map((order) => {
                      const identifier = order.showId || order.orderId;
                      let progressLabel = '—';
                      if (order.deliveredAt) {
                        const resolved = formatDate(order.deliveredAt);
                        progressLabel = resolved === '—' ? 'Delivered' : `Delivered · ${resolved}`;
                      } else if (order.pickedAt) {
                        const resolved = formatDate(order.pickedAt);
                        progressLabel = resolved === '—' ? 'Picked' : `Picked · ${resolved}`;
                      }
                      const customerName = order.customer?.name || '—';
                      const paymentMode = order.paymentMode || '—';
                      const isSelected = selectedOrderIds.includes(order.orderId);

                      return (
                        <tr key={order.orderId} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              checked={isSelected}
                              onChange={() => toggleOrderSelection(order.orderId)}
                            />
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{identifier}</td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{progressLabel}</td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{paymentMode}</td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{customerName}</td>
                          <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                            {formatCurrency(order.finalAmount)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                            {formatCurrency(order.commissionAmount)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                            {formatCurrency(order.payableAmount)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total amount</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(totals.totalAmount)}
                </p>
              </div>
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total commission</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(totals.commissionAmount)}
                </p>
              </div>
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total payable</p>
                <p className="text-lg font-semibold text-green-600">
                  {formatCurrency(totals.payableAmount)}
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {selectedOrderIds.length} order{selectedOrderIds.length === 1 ? '' : 's'} selected for settlement.
              </p>
              <button
                type="button"
                onClick={handleCreateSettlement}
                disabled={creating || selectedOrderIds.length === 0}
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-green-600 text-white font-medium shadow-sm hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating settlement...
                  </>
                ) : (
                  <>
                    <Wallet className="mr-2 h-4 w-4" />
                    Generate settlement
                  </>
                )}
              </button>
            </div>
          </section>

          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pending shop settlements</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Settlements awaiting approval or payment. Use the print action to generate shop copies.
                </p>
              </div>
            </div>
            <div className="relative overflow-x-auto">
              {loadingSettlements && (
                <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/60 flex items-center justify-center z-10">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
              )}
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Settlement</th>
                    <th className="px-4 py-3 text-left">Shop</th>
                    <th className="px-4 py-3 text-right">Order amount</th>
                    <th className="px-4 py-3 text-right">Commission</th>
                    <th className="px-4 py-3 text-right">Payable</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {settlements.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-gray-500 dark:text-gray-300">
                        No pending settlements found.
                      </td>
                    </tr>
                  ) : (
                    settlements.map((settlement) => {
                      const status = PAYMENT_STATUS_LABELS[settlement.paymentStatus || 0] || PAYMENT_STATUS_LABELS[0];
                      const rawPayable =
                        Number.parseFloat(settlement.totalOrderAmount || 0) -
                        Number.parseFloat(settlement.commissionAmount || 0);
                      const payableAmount = Number.isNaN(rawPayable)
                        ? 0
                        : Number(rawPayable.toFixed(2));
                      return (
                        <tr key={settlement.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900 dark:text-gray-100">#{settlement.id}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(settlement.settlementDate)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{settlement.shop?.name || '—'}</td>
                          <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                            {formatCurrency(settlement.totalOrderAmount)}
                          </td>
                          <td
                            className={`px-4 py-3 text-right text-gray-700 dark:text-gray-300 ${
                              editingCommissionId === settlement.id ? '' : 'cursor-pointer'
                            }`}
                            onDoubleClick={() => {
                              if (editingCommissionId === settlement.id) {
                                return;
                              }
                              startCommissionEditing(settlement);
                            }}
                          >
                            {editingCommissionId === settlement.id ? (
                              <form onSubmit={handleCommissionUpdate} className="flex flex-col items-end gap-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={commissionEditValue}
                                    onChange={(event) => setCommissionEditValue(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Escape') {
                                        event.preventDefault();
                                        cancelCommissionEditing();
                                      }
                                    }}
                                    className="w-24 rounded-md border border-gray-300 px-2 py-1 text-right text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900"
                                    autoFocus
                                  />
                                  <button
                                    type="submit"
                                    disabled={commissionUpdatingId === settlement.id}
                                    className="inline-flex items-center rounded-md bg-green-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {commissionUpdatingId === settlement.id ? (
                                      <>
                                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                        Updating
                                      </>
                                    ) : (
                                      'Update'
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelCommissionEditing}
                                    disabled={commissionUpdatingId === settlement.id}
                                    className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    Cancel
                                  </button>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Commission amount:{' '}
                                  {editingCommissionAmountPreview !== null
                                    ? formatCurrency(editingCommissionAmountPreview)
                                    : '—'}
                                </div>
                              </form>
                            ) : (
                              <div className="flex flex-col items-end">
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {formatCurrency(settlement.commissionAmount)}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {settlement.commissionPercentage !== undefined &&
                                  settlement.commissionPercentage !== null
                                    ? `${Number.parseFloat(settlement.commissionPercentage).toFixed(2)}%`
                                    : '—'}
                                </span>
                                <span className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                                  Double-click to edit
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                            {formatCurrency(payableAmount)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center space-x-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                                status.className
                              }`}
                            >
                              {settlement.paymentStatus === 1 ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                              <span>{status.label}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                type="button"
                                onClick={() => openEditModal(settlement)}
                                className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handlePrint(settlement.id)}
                                disabled={printingId === settlement.id}
                                className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-60"
                              >
                                {printingId === settlement.id ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Preparing...
                                  </>
                                ) : (
                                  <>
                                    <Printer className="mr-2 h-4 w-4" />
                                    Reprint
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
      </LayoutWithSidebar>

      {showEditModal && settlementToEdit && (
        <ShopSettlementEditModal
          isOpen={showEditModal}
          settlement={settlementToEdit}
          onClose={closeEditModal}
          onUpdated={handleSettlementUpdated}
        />
      )}
    </>
  );
}
